import type { VitalSignPayload } from "@medassist/shared";
import { BarChartIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const W = 100;
  const H = 32;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - ((v - min) / range) * H;
    return `${x},${y}`;
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full overflow-visible" aria-hidden="true">
      <polyline
        points={pts.join(" ")}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* last-point dot */}
      <circle
        cx={parseFloat(pts[pts.length - 1].split(",")[0])}
        cy={parseFloat(pts[pts.length - 1].split(",")[1])}
        r="2.5"
        fill="var(--accent)"
      />
    </svg>
  );
}

export function ChartsWidget({ events, onNavigate }: WidgetProps) {
  const hrEvents = events
    .filter((e) => e.eventType === "vital_sign" && (e.payload as unknown as VitalSignPayload).kind === "hr")
    .slice(0, 10)
    .reverse();

  const bpEvents = events
    .filter((e) => e.eventType === "vital_sign" && (e.payload as unknown as VitalSignPayload).kind === "bp")
    .slice(0, 10)
    .reverse();

  const hrValues = hrEvents.map((e) => (e.payload as unknown as VitalSignPayload).value ?? 0).filter((v) => v > 0);
  const latestHr = hrValues[hrValues.length - 1];
  const latestBp = bpEvents[bpEvents.length - 1]?.payload as unknown as VitalSignPayload | undefined;

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <BarChartIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Charts</h2>
        <button onClick={() => onNavigate("charts")} className="ml-auto text-xs text-(--accent) hover:underline">
          Full charts
        </button>
      </div>
      {hrValues.length >= 2 ? (
        <>
          <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>Heart rate (last {hrValues.length})</span>
            {latestHr != null && <span className="font-medium text-gray-900 dark:text-gray-100">{latestHr} bpm</span>}
          </div>
          <Sparkline values={hrValues} />
        </>
      ) : latestBp ? (
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Latest BP</p>
          <p className="font-medium text-gray-900 dark:text-gray-100">
            {latestBp.systolic ?? "?"}/{latestBp.diastolic ?? "?"} mmHg
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500">No vital readings yet — log some to see trends</p>
      )}
    </section>
  );
}
